import {
  EquipmentType,
  HeatingType,
  LivingRoomDirection,
  PersonalContribution,
  SolePlateOrBasement,
  type UpdateProjectBuyingHouseInput,
  type UpdateProjectNewApartmentInput,
  type UpdateProjectNewBuildingInput,
} from 'graphql/generated/graphql';
import requireValue from 'utils/requireValue';
import {
  type IFormCoordinates,
  optionalEnum,
  requireEnum,
  requireNumbers,
  toLatLngInput,
} from './utils';

// The values the calculator pages send and their mutation inputs (see `./utils`).

/** The values the apartment page sends: the wizard values with the enabled feature names and the step. */
export interface IApartmentFormData {
  features: string[];
  step?: number | null;
  type?: string | null;
  additional_features?: boolean | null;
  cost_apartment?: number | null;
  property_transfer_tax?: number | null;
  land_register_fee?: number | null;
  notary_fee?: number | null;
  broker_commission?: number | null;
  coordinates?: IFormCoordinates | null;
  other_investments_amount?: number | null;
  other_investments_comment?: string | null;
  [field: string]: unknown;
}

/** The mutation input: the required prices are set by the first step. */
export const toNewApartmentInput = (
  formData: IApartmentFormData,
): UpdateProjectNewApartmentInput => ({
  ...formData,
  cost_apartment: requireValue(formData.cost_apartment, 'cost_apartment'),
  property_transfer_tax: requireValue(formData.property_transfer_tax, 'property_transfer_tax'),
  land_register_fee: requireValue(formData.land_register_fee, 'land_register_fee'),
  notary_fee: requireValue(formData.notary_fee, 'notary_fee'),
  broker_commission: requireValue(formData.broker_commission, 'broker_commission'),
  coordinates: toLatLngInput(formData.coordinates),
});

/** The values the new building page sends: the wizard values with the feature names and the step. */
export interface IBuildHouseFormData {
  features: string[];
  step?: number | null;
  name?: string | null;
  bathrooms?: number | null;
  car_ports?: number | null;
  land_area?: number | null;
  has_property?: boolean | null;
  equipment_type?: string | null;
  floors?: number | null;
  garage_places?: number | null;
  heating_system_type?: string | null;
  living_room_direction?: string | null;
  living_space_by_floor?: unknown[];
  terrace_area?: number | null;
  demolition_area?: number | null;
  personal_contribution?: string | null;
  sole_plate_or_basement?: string | null;
  solar_power_equipment_type?: string | null;
  coordinates?: IFormCoordinates | null;
  other_investments_amount?: number | null;
  other_investments_comment?: string | null;
  [field: string]: unknown;
}

/**
 * The mutation input: the required fields are set by the steps' schemas (or saved with the
 * project), the selects only offer the enum values.
 */
export const toNewBuildingInput = (
  formData: IBuildHouseFormData,
): UpdateProjectNewBuildingInput => ({
  ...formData,
  name: requireValue(formData.name, 'name'),
  bathrooms: requireValue(formData.bathrooms, 'bathrooms'),
  car_ports: requireValue(formData.car_ports, 'car_ports'),
  land_area: requireValue(formData.land_area, 'land_area'),
  has_property: requireValue(formData.has_property, 'has_property'),
  equipment_type: requireEnum(EquipmentType, formData.equipment_type, 'equipment_type'),
  floors: requireValue(formData.floors, 'floors'),
  garage_places: requireValue(formData.garage_places, 'garage_places'),
  heating_system_type: requireEnum(
    HeatingType,
    formData.heating_system_type,
    'heating_system_type',
  ),
  living_room_direction: requireEnum(
    LivingRoomDirection,
    formData.living_room_direction,
    'living_room_direction',
  ),
  living_space_by_floor: requireNumbers(
    requireValue(formData.living_space_by_floor, 'living_space_by_floor'),
    'living_space_by_floor',
  ),
  terrace_area: requireValue(formData.terrace_area, 'terrace_area'),
  personal_contribution: optionalEnum(
    PersonalContribution,
    formData.personal_contribution,
    'personal_contribution',
  ),
  sole_plate_or_basement: optionalEnum(
    SolePlateOrBasement,
    formData.sole_plate_or_basement,
    'sole_plate_or_basement',
  ),
  solar_power_equipment_type: optionalEnum(
    EquipmentType,
    formData.solar_power_equipment_type,
    'solar_power_equipment_type',
  ),
  coordinates: toLatLngInput(formData.coordinates),
});

/** The values the buying house page sends: the wizard values with the feature names and the step. */
export interface IBuyingHouseFormData {
  features: string[];
  step?: number | null;
  type?: string | null;
  additional_features?: boolean | null;
  cost_house?: number | null;
  property_transfer_tax?: number | null;
  land_register_fee?: number | null;
  notary_fee?: number | null;
  broker_commission?: number | null;
  land_area?: number | null;
  house_area?: number | null;
  house_type?: string | null;
  number_of_floors?: number | null;
  construction_year?: number | null;
  number_of_toilets?: number | null;
  number_of_bathrooms?: number | null;
  coordinates?: IFormCoordinates | null;
  other_investments_amount?: number | null;
  other_investments_comment?: string | null;
  [field: string]: unknown;
}

/** The mutation input: the required fields are set by the first steps. */
export const toBuyingHouseInput = (
  formData: IBuyingHouseFormData,
): UpdateProjectBuyingHouseInput => ({
  ...formData,
  cost_house: requireValue(formData.cost_house, 'cost_house'),
  property_transfer_tax: requireValue(formData.property_transfer_tax, 'property_transfer_tax'),
  land_register_fee: requireValue(formData.land_register_fee, 'land_register_fee'),
  notary_fee: requireValue(formData.notary_fee, 'notary_fee'),
  broker_commission: requireValue(formData.broker_commission, 'broker_commission'),
  land_area: requireValue(formData.land_area, 'land_area'),
  house_area: requireValue(formData.house_area, 'house_area'),
  house_type: requireValue(formData.house_type, 'house_type'),
  number_of_floors: requireValue(formData.number_of_floors, 'number_of_floors'),
  construction_year: requireValue(formData.construction_year, 'construction_year'),
  number_of_toilets: requireValue(formData.number_of_toilets, 'number_of_toilets'),
  number_of_bathrooms: requireValue(formData.number_of_bathrooms, 'number_of_bathrooms'),
  coordinates: toLatLngInput(formData.coordinates),
});
